import React, { useContext, useEffect, useRef, useState } from 'react';
import { CBadge, CCard, CCol, CCollapse, CDataTable, CRow } from '@coreui/react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import TableStats from '../../shared/components/TableStats';
import { JobPhase, JobPhaseColor } from '../../shared/models/jobs';
import FeedbackRating from '../../shared/components/FeedbackRating';
import JobsDetailsContainer from '../../dashboard/components/JobsDetailsContainer';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import { selectInsightsData, selectLoadingInsightsStatus } from '../../store/selectors/insights.selectors';
import { TaskDetail, TransformedTask, WidgetName } from '../../shared/models/insights';
import convertTaskToJob from '../../shared/utils/insights-utils';
import diffAsString, { formatDuration } from '../../shared/utils/time-utils';

const METER_TO_MILES_RATIO = 1609;

const getTasksFilter = (filterName, filterValue) => {
  switch (filterName) {
    case WidgetName.Completed:
      return (value) => value.last_phase === JobPhase.Completed;
    case WidgetName.Canceled:
      return (value) => value.last_phase === JobPhase.Cancelled;
    case WidgetName.Feedback:
    case WidgetName.AvgRating:
      return (value) => +value.feedback_rating > 0;
    case WidgetName.FeedbackComment:
      return (value) => value.feedback_comment;
    case WidgetName.FeedbackRating:
      return (value) => filterValue ? +value.feedback_rating === +filterValue : +value.feedback_rating;
    case WidgetName.OnTimeDeliveries:
      return (value) => value.time_arrived <= value.due_time;
    default:
      return (value) => value;
  }
};

const getPageTitle = (filterName, filterValue) => {
  switch (filterName) {
    case WidgetName.Completed:
      return "DELIVERIES COMPLETED";
    case WidgetName.Canceled:
      return "DELIVERIES CANCELED";
    case WidgetName.Feedback:
    case WidgetName.AvgRating:
      return "DELIVERIES WITH FEEDBACK RATING";
    case WidgetName.FeedbackComment:
      return "DELIVERIES WITH FEEDBACK COMMENTS";
    case WidgetName.FeedbackRating:
      return `DELIVERIES WITH FEEDBACK RATING ${filterValue}`;
    case WidgetName.OnTimeDeliveries:
      return "ON TIME DELIVERIES";
    default:
      return "INSIGHTS DETAILS";
  }
};

const FIELDS = ({agent, task}) => [
  {
    key: 'appointment_id',
    label: `${ task } #`,
    filter: true,
    sorter: true,
  },
  {
    key: 'agent_name',
    label: agent,
    filter: true,
    sorter: true,
  },
  {
    key: 'destination',
    Label: 'Destination',
    filter: true,
    sorter: true,
  },
  {
    key: 'last_phase',
    label: 'Status',
    sorter: true,
    filter: true,
  },
  {
    key: 'time_on_site',
    label: 'Time On Site',
    filter: true,
    sorter: true,
  },
  {
    key: 'distance_traveled',
    label: 'Distance Traveled',
    filter: false,
    sorter: true,
  },
  {
    key: 'promised_time',
    label: 'Promised Time',
    filter: true,
    sorter: true,
  },
  {
    key: 'time_arrived',
    label: 'Arrived Time',
    filter: true,
    sorter: true,
  },
  {
    key: 'completed_time',
    label: 'Completed Time',
    filter: true,
    sorter: true,
  },
];

const InsightsTasksViewPage = () => {
  const { filterName, filterValue } = useParams<{filterName: string, filterValue?: string}>();
  const insights = useSelector(selectInsightsData)?.tasks;
  const isLoading = useSelector(selectLoadingInsightsStatus);
  const tableRef = useRef(null);
  const labels = useContext(ConfigContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState<number>(undefined);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [itemsCount, setItemsCount] = useState(0);

  const scrollToLeft = () => {
    tableRef?.current?.querySelector('.position-relative')?.scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  };

  const handleRowClick = (task: TaskDetail, index, columnName, e) => {
    if (columnName !== 'actions' || e.target.tagName.toLowerCase() === 'td') {
      const isOpenDetails = showDrawer !== task?.id;
      setShowDrawer(isOpenDetails ? task?.id : undefined);
      if (isOpenDetails) {
        scrollToLeft();
      }
    }
  };

  const transformTasks = (data) => {
    return data?.sort((a, b) => b.due_time - a.due_time).map((task :TaskDetail) => {
      const timeOnSite = task?.completed_time && task?.time_arrived && Math.floor((task?.completed_time - task?.time_arrived)/1000/60);
      const apptTo = +task?.metadata?.find(meta => meta.n === 'appt_to')?.v;
      const arrivedTime = moment(task.time_arrived);
      const dueTo = moment(apptTo);
      return {
        id: task.task_id,
        appointment_id: task?.metadata?.find(obj => obj.n === 'appointment_id')?.v || '',
        agent_name: task.agent_name || 'Unassigned',
        destination: task.dest_name,
        phase: task.last_phase,
        time_on_site: timeOnSite !== undefined && timeOnSite > 0 ? formatDuration(moment.duration(timeOnSite,  'minutes')) : '-',
        completed_time: moment(task.completed_time).format('l LT'),
        time_arrived: arrivedTime.format('l LT'),
        promised_time: apptTo ? moment(apptTo).format('l LT') : '-',
        feedback_rating: task.feedback_rating,
        distance_traveled: task.distance_traveled > 0 ? Math.round((task?.distance_traveled || 0) * 100 / METER_TO_MILES_RATIO) / 100 : '-',
        wait_time: arrivedTime.isSameOrBefore(dueTo) ? 'On Time' : diffAsString(dueTo, arrivedTime),
        task,
      } as TransformedTask;
    });
  };


  useEffect(() => {
    setFilteredTasks(transformTasks(insights?.filter(getTasksFilter(filterName, filterValue))));
  }, [filterName, insights, filterValue]);


  return (
    <>
      <CRow>
        <CCol lg="12">
          <div className='glympser-cardHeader justify-content-between mb-2'>
            <div className='glympser-cardTitle pl-0'>
              {getPageTitle(filterName, filterValue)}
            </div>
          </div>
          <CCard className="glympser-container--roundedWithShadow">
            <div className='insights-tasks-list-container' ref={tableRef}>
              <CDataTable
                items={ filteredTasks }
                fields={ FIELDS(labels) }
                loading={ isLoading }
                columnFilter
                hover
                sorter
                pagination={{
                  addListClass: "ml-2"
                }}
                onRowClick={ handleRowClick }
                onPageChange={(page) => setCurrentPage(page)}
                onFilteredItemsChange={(data) => setItemsCount(data?.length || 0)}
                underTableSlot={filteredTasks && filteredTasks.length > 0 && <TableStats page={currentPage} total={itemsCount} />}
                scopedSlots={{
                  'last_phase': ({ phase, feedback_rating }: TransformedTask) => {
                    return (
                      <td>
                        <CBadge color={ JobPhaseColor[phase] || 'secondary'}>{ phase }</CBadge>
                        <FeedbackRating rating={feedback_rating} containerClassName="mt-1 font-sm" />
                      </td>
                    );
                  },

                  'details': ({ task } : TransformedTask) => {
                    // eslint-disable-next-line react/destructuring-assignment
                    if (showDrawer !== task.task_id) {
                      return null;
                    }

                    return (
                      <CCollapse show>
                        <JobsDetailsContainer job={convertTaskToJob(task)} />
                      </CCollapse>
                    );
                  },
                }}
              />
            </div>
          </CCard>
        </CCol>
      </CRow>
    </>
  )
};

export default InsightsTasksViewPage;
